/**
 * PayoutSheet — Stories 2.5 + 2.6 (Payout-Modal mit Solana-Pay-QR)
 *
 * Referenz (AUTORITATIV): claude-design-import/settlements.html (Sheet) +
 * Entscheidungslog ADR-01: Q1 Solana Pay als Standard, zusaetzlich
 * Helium-Wallet-Kompatibilitaet wie Legacy-v1 als zweite Darstellung.
 *
 * - QR clientseitig via qrcode.react (SVG → verlustfrei scharf in jeder
 *   Groesse), Payloads AUSSCHLIESSLICH aus dem Contract-Feld
 *   settlement.payout (solana_pay_uri / legacy_uri) — nichts selbst gebaut.
 * - Umschalter Solana Pay ↔ Legacy (Helium v1); Legacy ist laut Contract nur
 *   fuer HNT-Settlements gesetzt und wird sonst deaktiviert angeboten.
 * - Nil-sicher: ohne hinterlegte Host-Wallet sind alle payout-Felder null →
 *   Leerzustand mit Hinweis statt QR.
 * - Wallet-Adresse mit Copy-Button (Fallback-Kopierfunktion, AC-3 Story 2.6),
 *   CSV-Export-Link und Beleg-PDF-Link.
 * - Status-Umschalter: draft → „Freigeben“, approved → „Als beglichen
 *   markieren“ (PATCH, optimistisch mit Rollback in useUpdateSettlementStatus).
 */
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { QRCodeSVG } from 'qrcode.react'
import { Check, Copy, Download, FileText } from 'lucide-react'
import { ModalSheet } from '../../components/ui/ModalSheet'
import { useSettlementDetail, type Settlement } from './useSettlements'
import {
  useUpdateSettlementStatus,
  downloadSettlementsCsv,
  downloadSettlementDocument,
} from './useSettlementMutations'
import { fmtAmount, fmtDecimal, fmtPeriod, hasWallet, nextStatus } from './settlementUtils'

type QrVariant = 'solana' | 'legacy'

const QR_SIZE = 180

function shortAddress(address: string): string {
  if (address.length <= 16) return address
  return `${address.slice(0, 8)}…${address.slice(-6)}`
}

export function PayoutSheet({
  settlement: listSettlement,
  open,
  onClose,
}: {
  /** Zeile aus der Liste — sofort anzeigbar, Detail laedt nach. */
  settlement: Settlement | undefined
  open: boolean
  onClose: () => void
}) {
  const { t, i18n } = useTranslation('settlements')

  // Detail nachladen: nur GET /:id liefert calculation_breakdown; payout ist
  // in Liste und Detail enthalten — das Sheet rendert sofort aus der Zeile.
  const detail = useSettlementDetail(open ? listSettlement?.id : undefined)

  // F8 (Welle-4b): Die Antwort des Status-PATCH ist die verbindlichste Quelle —
  // sie gilt sofort, unabhaengig davon, ob Detail-Query oder Listen-Refetch
  // schon durch sind. Frueher blieb der alte Status stehen (und bot die Aktion
  // erneut an), wenn die Detail-Query danach nicht (mehr) lud.
  const [mutated, setMutated] = useState<Settlement | null>(null)
  const settlement = mutated ?? detail.settlement ?? listSettlement

  const [variant, setVariant] = useState<QrVariant>('solana')
  const [copied, setCopied] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Beim (Wieder-)Oeffnen zuruecksetzen — Render-Zeit-Reset statt Effekt
  // (React-Muster „adjusting state when props change“, lint-konform).
  const resetKey = open ? (listSettlement?.id ?? -1) : null
  const [prevResetKey, setPrevResetKey] = useState<number | null>(null)
  if (prevResetKey !== resetKey) {
    setPrevResetKey(resetKey)
    if (resetKey !== null) {
      setVariant('solana')
      setCopied(false)
      setActionError(null)
      setMutated(null)
    }
  }

  useEffect(
    () => () => {
      if (copyTimer.current) clearTimeout(copyTimer.current)
    },
    [],
  )

  const updateStatus = useUpdateSettlementStatus()

  if (!settlement) return null

  const payout = settlement.payout
  const walletAddress = payout?.wallet_address ?? null
  const solanaUri = payout?.solana_pay_uri ?? null
  const legacyUri = payout?.legacy_uri ?? null
  const qrValue = variant === 'legacy' ? legacyUri : solanaUri

  const period = fmtPeriod(settlement.period_month, i18n.language)
  const gb = fmtDecimal(settlement.gigabytes_transferred, 1)
  const hnt = fmtDecimal(settlement.hnt_rewards, 2)
  const amount = fmtAmount(settlement.total_amount, settlement.currency)

  // Detail-Query gescheitert und kein bestaetigter Mutations-Stand: der
  // sichtbare Status stammt nur noch aus der (moeglicherweise veralteten)
  // Listenkopie — dann KEINE Statusaktion anbieten, sondern Fehler zeigen (F8).
  const detailFailed = detail.isError && mutated === null
  const target = detailFailed ? null : nextStatus(settlement.status)

  const copyAddress = async () => {
    if (!walletAddress) return
    try {
      await navigator.clipboard.writeText(walletAddress)
      setCopied(true)
      if (copyTimer.current) clearTimeout(copyTimer.current)
      copyTimer.current = setTimeout(() => setCopied(false), 2000)
    } catch {
      setActionError(t('sheet.copyFailed'))
    }
  }

  const changeStatus = async () => {
    if (settlement.id === undefined || !target) return
    setActionError(null)
    try {
      const updated = await updateStatus.mutateAsync({ id: settlement.id, status: target })
      if (updated) setMutated(updated)
    } catch {
      // Rollback ist bereits im Hook passiert — hier nur die Meldung.
      setActionError(t('sheet.statusFailed'))
    }
  }

  const exportCsv = async () => {
    setActionError(null)
    try {
      await downloadSettlementsCsv({
        month: settlement.period_month,
        hostId: settlement.host_id,
      })
    } catch {
      setActionError(t('sheet.csvFailed'))
    }
  }

  const downloadPdf = async () => {
    if (settlement.id === undefined) return
    setActionError(null)
    try {
      await downloadSettlementDocument(settlement.id, settlement.invoice_number)
    } catch {
      setActionError(t('sheet.pdfFailed'))
    }
  }

  return (
    <ModalSheet
      open={open}
      onClose={onClose}
      title={t('sheet.title', { host: settlement.host_name ?? t('noValue') })}
      description={
        period && gb && hnt
          ? t('sheet.subtitle', { period, gb, hnt })
          : (period ?? undefined)
      }
      variant="dialog"
      size="sm"
      data-testid="payout-sheet"
    >
      {/* --- QR-Karte -------------------------------------------------------- */}
      <div className="card card-pad stl-qr-card">
        {hasWallet(settlement) && qrValue ? (
          <>
            <div
              className="segment stl-qr-segment"
              role="group"
              aria-label={t('sheet.qrVariantAria')}
            >
              <button
                type="button"
                className={variant === 'solana' ? 'is-active' : undefined}
                aria-pressed={variant === 'solana'}
                onClick={() => setVariant('solana')}
                data-testid="payout-qr-solana"
              >
                {t('sheet.qrSolana')}
              </button>
              <button
                type="button"
                className={variant === 'legacy' ? 'is-active' : undefined}
                aria-pressed={variant === 'legacy'}
                onClick={() => setVariant('legacy')}
                disabled={!legacyUri}
                title={!legacyUri ? t('sheet.legacyUnavailable') : undefined}
                data-testid="payout-qr-legacy"
              >
                {t('sheet.qrLegacy')}
              </button>
            </div>

            {/* Weisser Rahmen: QR braucht hellen Grund + Ruhezone, auch im Dark-Theme. */}
            <div className="stl-qr-frame" data-testid="payout-qr" data-variant={variant}>
              <QRCodeSVG value={qrValue} size={QR_SIZE} level="M" marginSize={2} />
            </div>
            <p className="muted stl-qr-hint">
              {variant === 'legacy' ? t('sheet.scanHintLegacy') : t('sheet.scanHintSolana')}
            </p>
          </>
        ) : (
          /* Leerzustand (Story 2.6): keine Wallet hinterlegt → kein QR. */
          <div className="stl-qr-empty" data-testid="payout-qr-empty">
            <p className="stl-qr-empty-title">{t('sheet.noWalletTitle')}</p>
            <p className="muted stl-qr-empty-hint">{t('sheet.noWalletHint')}</p>
          </div>
        )}

        {walletAddress && (
          <button
            type="button"
            className="addr-chip stl-addr-chip"
            onClick={() => void copyAddress()}
            title={t('sheet.copyTitle')}
            aria-label={t('sheet.copyAria', { address: walletAddress })}
            data-testid="payout-copy-address"
          >
            <span className="mono">{shortAddress(walletAddress)}</span>
            {copied ? (
              <Check aria-hidden="true" size={12} className="stl-copy-ok" />
            ) : (
              <Copy aria-hidden="true" size={12} />
            )}
            <span className="ui-sr-only" role="status">
              {copied ? t('sheet.copied') : ''}
            </span>
          </button>
        )}
      </div>

      {/* --- Betrag ---------------------------------------------------------- */}
      <div className="stat-strip stl-sheet-stats">
        <div className="stat">
          <div className="stat-label">{t('sheet.amount')}</div>
          <div className="stat-value num-md" data-testid="payout-amount">
            {amount ?? t('noValue')}
          </div>
        </div>
        <div className="stat">
          <div className="stat-label">{t('sheet.invoiceNumber')}</div>
          <div className="stat-value stl-invoice mono" data-testid="payout-invoice">
            {settlement.invoice_number ?? t('noValue')}
          </div>
        </div>
      </div>

      {detailFailed && (
        <p className="stl-sheet-error" role="alert" data-testid="payout-detail-error">
          {t('sheet.detailFailed')}
        </p>
      )}

      {actionError && (
        <p className="stl-sheet-error" role="alert" data-testid="payout-error">
          {actionError}
        </p>
      )}

      {/* --- Aktionen -------------------------------------------------------- */}
      <div className="stl-sheet-actions">
        {target ? (
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void changeStatus()}
            disabled={updateStatus.isPending}
            data-testid="payout-status-action"
          >
            {updateStatus.isPending
              ? t('sheet.statusPending')
              : t(target === 'approved' ? 'sheet.approve' : 'sheet.markSettled')}
          </button>
        ) : detailFailed ? null : (
          <p className="muted stl-settled-note" data-testid="payout-settled-note">
            {t('sheet.settledNote')}
          </p>
        )}
        <button
          type="button"
          className="btn"
          onClick={() => void exportCsv()}
          data-testid="payout-csv"
        >
          <Download aria-hidden="true" size={14} />
          {t('sheet.csvExport')}
        </button>
        <button
          type="button"
          className="btn"
          onClick={() => void downloadPdf()}
          disabled={settlement.id === undefined}
          data-testid="payout-pdf"
        >
          <FileText aria-hidden="true" size={14} />
          {t('sheet.pdfDocument')}
        </button>
      </div>

      <p className="muted stl-sheet-footnote">{t('sheet.footnote')}</p>
    </ModalSheet>
  )
}
